import { filter, forEach, includes, isNil } from 'ramda';

// Replacement for `element.scrollIntoView()` when bringing a widget card of the right
// panel into view.
//
// `scrollIntoView` scrolls EVERY scrollable ancestor, including those in
// `overflow: hidden` / `overflow: clip`. Such containers are scrollable programmatically
// (`scrollTop` can be assigned) but not by the user (no scrollbar, no wheel): whatever
// gets pushed out of their box becomes permanently unreachable. That is what made the
// editor header (breadcrumb, history button, title) disappear for good.
//
// We therefore only scroll containers the user could actually scroll back, plus the
// window: any movement caused by a click stays reversible.

const USER_SCROLLABLE_OVERFLOWS = ['auto', 'scroll'];

const getComputedOverflowY = (element) => window.getComputedStyle(element).overflowY;

// A container in `overflow: hidden` / `clip` is still scrollable programmatically, so
// testing `scrollHeight > clientHeight` alone is not enough: we also have to make sure
// the user could perform the opposite gesture.
const isUserScrollableY = (getOverflowY) => (element) =>
  includes(getOverflowY(element), USER_SCROLLABLE_OVERFLOWS) &&
  element.scrollHeight > element.clientHeight;

// `getOverflowY` is injectable for tests: jsdom implements no layout, so
// `getComputedStyle` always returns default values there.
export const collectScrollableAncestors = (element, getOverflowY = getComputedOverflowY) => {
  const ancestors = [];
  let parent = isNil(element) ? null : element.parentElement;

  // Stop before `body` / `html`: the page scroll is handled separately, through the
  // window, and is always recoverable by the user.
  while (!isNil(parent) && parent !== window.document.body && parent !== window.document.documentElement) {
    ancestors.push(parent);
    parent = parent.parentElement;
  }

  // Innermost first: every inner scroll is applied synchronously, so the positions read
  // on the next iteration are already up to date.
  return filter(isUserScrollableY(getOverflowY), ancestors);
};

// Scroll position to apply to the container so that the element is vertically centered.
// Borders are ignored (the containers involved have none): `containerTop` is the top of
// the border box, not the top of the scrollport.
export const computeCenteredScrollTop = ({
  containerScrollTop,
  containerTop,
  containerClientHeight,
  elementTop,
  elementHeight
}) => {
  const elementOffsetInContent = (elementTop - containerTop) + containerScrollTop;
  const centeringOffset = (containerClientHeight - elementHeight) / 2;

  return Math.max(0, elementOffsetInContent - centeringOffset);
};

const isFullyVisibleIn = ({ top, bottom }, element) => {
  const rect = element.getBoundingClientRect();

  return rect.top >= top && rect.bottom <= bottom;
};

const scrollElementTo = (element, top) => {
  // `scrollTo` is not available everywhere (jsdom in particular): `scrollTop` is the
  // fallback.
  if (typeof element.scrollTo === 'function') {
    element.scrollTo({ top, behavior: 'auto' });
    return;
  }

  element.scrollTop = top;
};

const centerInAncestor = (element) => (container) => {
  const containerRect = container.getBoundingClientRect();

  if (isFullyVisibleIn({ top: containerRect.top, bottom: containerRect.bottom }, element)) return;

  const elementRect = element.getBoundingClientRect();

  scrollElementTo(container, computeCenteredScrollTop({
    containerScrollTop: container.scrollTop,
    containerTop: containerRect.top,
    containerClientHeight: container.clientHeight,
    elementTop: elementRect.top,
    elementHeight: elementRect.height
  }));
};

const centerInViewport = (element) => {
  if (isFullyVisibleIn({ top: 0, bottom: window.innerHeight }, element)) return;

  const elementRect = element.getBoundingClientRect();

  window.scrollTo({
    top: computeCenteredScrollTop({
      containerScrollTop: window.scrollY,
      containerTop: 0,
      containerClientHeight: window.innerHeight,
      elementTop: elementRect.top,
      elementHeight: elementRect.height
    }),
    behavior: 'smooth'
  });
};

export const scrollToCard = (element) => {
  if (isNil(element)) return;

  // Inner containers scroll instantly, so that the following measurements are taken on
  // already settled positions, then the window scrolls smoothly.
  forEach(centerInAncestor(element), collectScrollableAncestors(element));

  centerInViewport(element);
};
